import { supabase } from '../lib/supabase.js';

// Get all pro tips with optional filtering
export const getProTips = async (filters = {}) => {
  try {
    let query = supabase
      ?.from('pro_tips')
      ?.select('*')
      ?.eq('is_active', true)
      ?.order('category', { ascending: true })
      ?.order('display_order', { ascending: true });

    // Apply filters
    if (filters?.category) {
      query = query?.eq('category', filters?.category);
    }

    if (filters?.search) {
      query = query?.or(`title.ilike.%${filters?.search}%,content.ilike.%${filters?.search}%`);
    }

    const { data, error } = await query;

    if (error) throw error;
    return { data: data || [], error: null };
  } catch (error) {
    return { data: [], error: error?.message };
  }
};

// Get pro tips by category
export const getProTipsByCategory = async (category) => {
  try {
    const { data, error } = await supabase
      ?.from('pro_tips')
      ?.select('*')
      ?.eq('category', category)
      ?.eq('is_active', true)
      ?.order('display_order', { ascending: true });

    if (error) throw error;
    return { data: data || [], error: null };
  } catch (error) {
    return { data: [], error: error?.message };
  }
};

// Create a new pro tip
export const createProTip = async (proTipData) => {
  try {
    // Get the next display order for the category
    const { data: existingTips } = await supabase
      ?.from('pro_tips')
      ?.select('display_order')
      ?.eq('category', proTipData?.category)
      ?.eq('is_active', true)
      ?.order('display_order', { ascending: false })
      ?.limit(1);

    const nextDisplayOrder = (existingTips?.[0]?.display_order || 0) + 1;

    const { data, error } = await supabase
      ?.from('pro_tips')
      ?.insert([{
        title: proTipData?.title,
        content: proTipData?.content,
        category: proTipData?.category,
        display_order: nextDisplayOrder,
        is_active: true,
        created_by: proTipData?.created_by
      }])
      ?.select('*')
      ?.single();

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    return { data: null, error: error?.message };
  }
};

// Update a pro tip
export const updateProTip = async (proTipId, updates) => {
  try {
    const validUpdates = {
      title: updates?.title,
      content: updates?.content,
      category: updates?.category,
      display_order: updates?.display_order,
      is_active: updates?.is_active,
      updated_by: updates?.updated_by,
      updated_at: new Date()?.toISOString()
    };

    // Remove undefined values
    Object.keys(validUpdates)?.forEach(key => {
      if (validUpdates?.[key] === undefined) {
        delete validUpdates?.[key];
      }
    });

    const { data, error } = await supabase
      ?.from('pro_tips')
      ?.update(validUpdates)
      ?.eq('id', proTipId)
      ?.select('*')
      ?.single();

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    return { data: null, error: error?.message };
  }
};

// Delete a pro tip
export const deleteProTip = async (proTipId) => {
  try {
    const { error } = await supabase
      ?.from('pro_tips')
      ?.delete()
      ?.eq('id', proTipId);

    if (error) throw error;
    return { error: null };
  } catch (error) {
    return { error: error?.message };
  }
};

// Reorder pro tips within a category
export const reorderProTips = async (tipUpdates, updatedBy) => {
  try {
    const updates = tipUpdates?.map(update => ({
      id: update?.id,
      display_order: update?.display_order,
      updated_by: updatedBy,
      updated_at: new Date()?.toISOString()
    }));

    const { data, error } = await supabase
      ?.from('pro_tips')
      ?.upsert(updates)
      ?.select('*');

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    return { data: null, error: error?.message };
  }
};

// Get pro tip statistics
export const getProTipStatistics = async () => {
  try {
    const { data, error } = await supabase
      ?.from('pro_tips')
      ?.select('category')
      ?.eq('is_active', true);

    if (error) throw error;

    const stats = {
      total_tips: data?.length || 0,
      by_category: {}
    };

    data?.forEach(tip => {
      if (tip?.category) {
        stats.by_category[tip.category] = 
          (stats?.by_category?.[tip?.category] || 0) + 1;
      }
    });

    return { data: stats, error: null };
  } catch (error) {
    return { data: null, error: error?.message };
  }
};

const ProTipService = {
  getProTips,
  getProTipsByCategory,
  createProTip,
  updateProTip,
  deleteProTip,
  reorderProTips,
  getProTipStatistics
};

export default ProTipService;